import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mail, HelpCircle, Book, Bug, ArrowLeft, Send, Phone, Clock, CheckCircle } from 'lucide-react';

const Support = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        category: 'general',
        message: ''
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        // Simulate form submission
        setTimeout(() => {
            setIsSubmitting(false);
            setIsSubmitted(true);
        }, 1500);
    };

    const faqItems = [
        {
            question: "How do I enable artist status on my account?",
            answer: "You can enable artist status in your profile settings. Navigate to your profile, click 'Edit Profile', and toggle the 'Artist Status' option. This will unlock features like music uploads, enhanced profiles, and revenue opportunities."
        },
        {
            question: "What file formats are supported for uploads?",
            answer: "We support MP3, WAV, and FLAC for audio; MP4, AVI, and MOV for video; and JPG, PNG, and GIF for images. Files can be up to 1GB in size."
        },
        {
            question: "How do play counts and analytics work?",
            answer: "Play counts update in real-time when users play your content. Analytics track plays, views, completion rates, and user engagement. You can view detailed statistics in your dashboard."
        },
        {
            question: "Can I collaborate with other artists on the platform?",
            answer: "Yes! Use the Contributing Artists feature when uploading content to credit collaborators. All credited artists will share in analytics and can be featured together."
        },
        {
            question: "How do I report inappropriate content or users?",
            answer: "Use the report buttons on content or profiles, or contact our support team directly. We review all reports and take appropriate action to maintain community standards."
        },
        {
            question: "What happens if I delete my account?",
            answer: "Account deletion is permanent and removes all your content, analytics, and data from the platform. This action cannot be undone, so please download any content you want to keep first."
        }
    ];

    const supportCategories = [
        { value: 'general', label: 'General Question' },
        { value: 'technical', label: 'Technical Issue' },
        { value: 'account', label: 'Account Problem' },
        { value: 'content', label: 'Content/Upload Issue' },
        { value: 'billing', label: 'Billing/Payment' },
        { value: 'report', label: 'Report Abuse' },
        { value: 'feature', label: 'Feature Request' },
        { value: 'bug', label: 'Bug Report' }
    ];

    return (
        <div className="min-h-screen bg-hero">
            {/* Header */}
            <section className="py-12 px-6 bg-gradient-to-b from-gray-900/80 to-transparent">
                <div className="max-w-4xl mx-auto">
                    <Link 
                        to="/"
                        className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors duration-300 mb-8"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back to Home
                    </Link>
                    
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-6">
                            <HelpCircle className="w-16 h-16 text-blue-400" />
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black gradient-text mb-6">
                            Support Center
                        </h1>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            Get help with The Deep Wave platform. Find answers to common questions or contact our support team.
                        </p>
                    </div>
                </div>
            </section>

            {/* Quick Support Options */}
            <section className="py-12 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-3 gap-6 mb-12">
                        <div className="glass-card p-6 text-center hover-lift">
                            <Book className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-white mb-3">Documentation</h3>
                            <p className="text-gray-400 mb-4">
                                Browse our comprehensive guides and tutorials for using the platform.
                            </p>
                            <div className="btn-ghost inline-flex items-center gap-2">
                                <Book className="w-4 h-4" />
                                Coming Soon
                            </div>
                        </div>
                        
                        <div className="glass-card p-6 text-center hover-lift">
                            <MessageCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-white mb-3">Community Forums</h3>
                            <p className="text-gray-400 mb-4">
                                Connect with other artists and get help from the community.
                            </p>
                            <div className="btn-ghost inline-flex items-center gap-2">
                                <MessageCircle className="w-4 h-4" />
                                Coming Soon
                            </div>
                        </div>
                        
                        <div className="glass-card p-6 text-center hover-lift">
                            <Bug className="w-12 h-12 text-red-400 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-white mb-3">Bug Reports</h3>
                            <p className="text-gray-400 mb-4">
                                Report technical issues and help us improve the platform.
                            </p>
                            <Link to="#contact" className="btn-primary inline-flex items-center gap-2">
                                <Bug className="w-4 h-4" />
                                Report Issue
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-12 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-black gradient-text mb-4">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-lg text-gray-400">
                            Find quick answers to the most common questions about The Deep Wave platform.
                        </p>
                    </div>
                    
                    <div className="space-y-6">
                        {faqItems.map((item, index) => (
                            <div key={index} className="glass-card p-6">
                                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                    <HelpCircle className="w-5 h-5 text-blue-400" />
                                    {item.question}
                                </h3>
                                <p className="text-gray-400 leading-relaxed pl-7">
                                    {item.answer}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form */}
            <section id="contact" className="py-12 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-black gradient-text mb-4">
                            Contact Support Team
                        </h2>
                        <p className="text-lg text-gray-400">
                            Can't find what you're looking for? Send us a message and we'll get back to you.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Contact Info */}
                        <div className="space-y-6">
                            <div className="glass-card p-6">
                                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                                    <Clock className="w-6 h-6 text-blue-400" />
                                    Response Times
                                </h3>
                                <div className="space-y-3 text-gray-400">
                                    <div className="flex justify-between">
                                        <span>General Questions:</span>
                                        <span className="text-green-400">24-48 hours</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Technical Issues:</span>
                                        <span className="text-yellow-400">12-24 hours</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Bug Reports:</span>
                                        <span className="text-orange-400">4-8 hours</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Critical Issues:</span>
                                        <span className="text-red-400">1-4 hours</span>
                                    </div>
                                </div>
                            </div>

                            <div className="glass-card p-6">
                                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                                    <Mail className="w-6 h-6 text-purple-400" />
                                    Other Ways to Reach Us
                                </h3>
                                <div className="space-y-3 text-gray-400">
                                    <div className="flex items-center gap-3">
                                        <Mail className="w-4 h-4 text-blue-400" />
                                        <span>support@thedeepwave.com</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Phone className="w-4 h-4 text-green-400" />
                                        <span>Available for critical issues only</span>
                                    </div>
                                </div>
                            </div>

                            <div className="glass-card p-6">
                                <h3 className="text-xl font-bold text-white mb-4">
                                    Before You Contact Us
                                </h3>
                                <ul className="space-y-2 text-gray-400">
                                    <li>• Check the FAQ section above</li>
                                    <li>• Try refreshing the page or clearing your browser cache</li>
                                    <li>• Include specific error messages if any</li>
                                    <li>• Mention your browser and operating system</li>
                                </ul>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="glass-card p-6">
                            {isSubmitted ? (
                                <div className="text-center py-12">
                                    <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
                                    <h3 className="text-2xl font-bold text-white mb-4">Message Sent!</h3>
                                    <p className="text-gray-400 mb-6">
                                        Thank you for contacting us. We'll get back to you within 24-48 hours.
                                    </p>
                                    <button 
                                        onClick={() => {
                                            setIsSubmitted(false);
                                            setFormData({
                                                name: '',
                                                email: '',
                                                subject: '',
                                                category: 'general',
                                                message: ''
                                            });
                                        }}
                                        className="btn-primary"
                                    >
                                        Send Another Message
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                                            placeholder="Your name"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                                            placeholder="your.email@example.com"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Category
                                        </label>
                                        <select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                                        >
                                            {supportCategories.map(cat => (
                                                <option key={cat.value} value={cat.value}>
                                                    {cat.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Subject
                                        </label>
                                        <input
                                            type="text"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                                            placeholder="Brief description of your issue"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Message
                                        </label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            required
                                            rows={6}
                                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                                            placeholder="Please provide as much detail as possible about your question or issue..."
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full btn-primary flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-5 h-5" />
                                                Send Message
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Support;